import BlockIcon                   from '@/assets/icons/BlockIcon.tsx';
import { Box }                     from '@chakra-ui/react';
import { Image as ChakraImage }    from '@chakra-ui/react';
import { ImageProps }              from '@chakra-ui/react';
import Spinner                     from '@/components/spinner/Spinner.tsx';
import { Text }                    from '@chakra-ui/react';
import { useTranslation }          from 'react-i18next';
import ContactsIcon                from '@/assets/icons/ContactsIcon';
import { FileUploadRoot }          from "@/components/ui/file-button";
import { FileUploadTrigger }       from "@/components/ui/file-button";
import EditIcon                    from '@/assets/icons/EditIcon';
import DeleteIcon                  from '@/assets/icons/DeleteIcon';
import { observer }                from 'mobx-react';
import React                       from 'react';
import useScreenSize               from '@/hooks/useScreenSize';

type Props = ImageProps & {
	actions?: {
		edit: (file: File) => void,
		delete: () => void,
	}
	errorSize?: 'sm' | 'xl';
	sourceIsLoading?: boolean;
	type?: 'profile'
};

const Image = observer((props: Props) => {
	const { actions, errorSize, sourceIsLoading, type } = props;
	const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loading');
	const { t } = useTranslation();
	const { isLG } = useScreenSize();
	const isClickable = !!actions;
	const source = props.src || '';
	const noImage = !source || status === 'error';

	const statusComponent = (() => {
		if (sourceIsLoading || status === 'loading') {
			return <Spinner size="inherit" />;
		}

		if (status === 'error') {
			if (errorSize === 'xl') {
				return <Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					textAlign="center"
					gap="5px"
					bg="gray.100"
					padding="5px"
					borderRadius="8px"
					height="100%"
				>
					{type === 'profile' ? <ContactsIcon size="md" /> : <BlockIcon />}
					<Text fontSize='10px'>{type === 'profile' ? t('app.noProfilePicture') : t('app.noImage')}</Text>
				</Box>
			}
			if (errorSize === 'sm') {
				return <Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					bg="gray.100"
					borderRadius="8px"
					height="100%"
				>
					{type === 'profile' ? <ContactsIcon size="sm" /> : <BlockIcon />}
				</Box>
			}
			return <BlockIcon />;
		}

		return null;
	})();

	const Content = () => {
				
		return (
			<Box 
				position="relative"
				width="100%"
				height="100%"
			>
				{isClickable && <Box
					display="flex"
					position="absolute"
					cursor="pointer"
					width="100%"
					height="100%"
					_hover={{ opacity: 0.8 }}
					opacity={0}
					borderRadius="8px"
					zIndex={1}
				>
					<FileUploadRoot 
						alignItems="stretch" 
						maxFiles={1}
						accept={["image/jpeg", "image/png"]}
						onFileAccept={(fileAcceptDetails) => {
							const { files } = fileAcceptDetails;
							const file = files[0];
							actions?.edit(file);
						}}
						width={isLG && source ? "50%" : "100%"}
						height="100%"
					>	
						<FileUploadTrigger 
							asChild
						>
							<Box 
								display="flex"
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
								backgroundColor="rgba(0, 0, 0, 0.6)"
								height="100%"
								borderRadius={!isLG || noImage ? "8px" : "8px 0 0 8px"}
							>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									width="100%"
									height="80%"
									borderRight={isLG && source ? "1px solid rgba(0, 0, 0, 1)" : "unset"}
								>
									<EditIcon color="white" size="xl" />
								</Box>
								
							</Box>
						</FileUploadTrigger>
					</FileUploadRoot>
					<Box 
						hidden={!source}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						hideBelow="lg"
						backgroundColor="rgba(0, 0, 0, 0.6)"
						borderRadius="0 8px 8px 0"
						width="50%"
						height="100%"
						onClick={() => actions.delete()}
					>
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							width="100%"
							height="80%"
							borderLeft="1px solid rgba(0, 0, 0, 1)"
						>
							<DeleteIcon color="white" size="xl" />
						</Box>
					</Box>
				</Box>}

				{statusComponent}

				<ChakraImage
					hidden={status === 'error'}
					onLoad={() => setStatus('loaded')}
					onError={() => setStatus('error')}
					style={{
						opacity: status === 'loaded' ? 1 : 0, // Change d'opacité après le chargement
						transition: "opacity 0.5s ease-in-out", // Applique une transition fluide
					}}
					{...props}
					src={source}
				/>
			</Box>
		)
	};

	const width = props.width || props.w || undefined;
	const height = props.height || props.h || undefined;
	const borderRadius = props.borderRadius || undefined;

	if (width) {
		return <Box w={width} h={height} borderRadius={borderRadius}><Content /></Box>;
	}

	return <Content />;
});

export default Image;